/* DOLO'S WAY: NIGHT MAZE — renderer. The static maze (floor, walls, decor,
   hideouts, lamps) is pre-rendered once to an offscreen canvas; every frame
   blits that, draws pickups, traps, runners and the horde, then cuts light
   holes through a darkness mask and paints the HUD strip. */
import { CELL, COLS, EXITS, H, MAZE, NIGHT_FRAMES, POWER_ITEMS, ROWS, SLASH_FRAMES, W } from "./constants";

const MONO = '"JetBrains Mono", monospace';
const HEADING = '"Chivo", sans-serif';

/* deterministic tiny hash so the floor noise never changes between loads */
const hash01 = (x, y) => {
  let h = (x * 374761393 + y * 668265263) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967295;
};

const tileOf = (x, y) => [Math.floor(x / CELL), Math.floor(y / CELL)];

/* ---------- static layer ---------- */
export function buildStatic(sprites, skin) {
  const c = document.createElement("canvas");
  c.width = W;
  c.height = H;
  const x = c.getContext("2d");

  // asphalt floor with a faint checker and rubble speckle — tinted by tonight's sky
  x.fillStyle = skin?.floor || "#08080d";
  x.fillRect(0, 0, W, H);
  for (let ty = 0; ty < ROWS; ty += 1) {
    for (let tx = 0; tx < COLS; tx += 1) {
      if (MAZE[ty][tx] === "#") continue;
      if ((tx + ty) % 2 === 0) {
        x.fillStyle = "rgba(255,255,255,0.016)";
        x.fillRect(tx * CELL, ty * CELL, CELL, CELL);
      }
      const n = hash01(tx, ty);
      if (n > 0.55) {
        x.fillStyle = `rgba(255,255,255,${0.02 + (n - 0.55) * 0.06})`;
        x.fillRect(tx * CELL + (n * 977) % (CELL - 3), ty * CELL + (n * 613) % (CELL - 3), 2, 2);
      }
    }
  }

  // walls — alternating stone and brick skins
  for (let ty = 0; ty < ROWS; ty += 1) {
    for (let tx = 0; tx < COLS; tx += 1) {
      if (MAZE[ty][tx] !== "#") continue;
      const img = sprites.walls[(tx + ty) % sprites.walls.length];
      if (img?.complete && img.naturalWidth) {
        x.drawImage(img, tx * CELL, ty * CELL, CELL, CELL);
      } else {
        x.fillStyle = (tx + ty) % 2 ? "#17130f" : "#131017";
        x.fillRect(tx * CELL, ty * CELL, CELL, CELL);
      }
    }
  }

  // wall faces catch a little ambient spill so the maze reads at night
  x.strokeStyle = "rgba(255, 90, 110, 0.12)";
  x.lineWidth = 1.5;
  for (let ty = 0; ty < ROWS; ty += 1) {
    for (let tx = 0; tx < COLS; tx += 1) {
      if (MAZE[ty][tx] !== "#") continue;
      if (ty + 1 < ROWS && MAZE[ty + 1][tx] !== "#") {
        x.beginPath();
        x.moveTo(tx * CELL, (ty + 1) * CELL - 1);
        x.lineTo((tx + 1) * CELL, (ty + 1) * CELL - 1);
        x.stroke();
      }
      if (tx + 1 < COLS && MAZE[ty][tx + 1] !== "#") {
        x.beginPath();
        x.moveTo((tx + 1) * CELL - 1, ty * CELL);
        x.lineTo((tx + 1) * CELL - 1, (ty + 1) * CELL);
        x.stroke();
      }
    }
  }

  // scattered wasteland decor on quiet floor tiles (non-blocking, dim)
  const decor = sprites.decor.filter((d) => d?.complete && d.naturalWidth);
  if (decor.length) {
    for (let ty = 1; ty < ROWS - 1; ty += 1) {
      for (let tx = 1; tx < COLS - 1; tx += 1) {
        if (MAZE[ty][tx] !== ".") continue;
        const n = hash01(tx * 3 + 11, ty * 7 + 5);
        if (n < 0.9) continue;
        const img = decor[Math.floor(n * 1000) % decor.length];
        const size = 20 + Math.floor(n * 30) % 10;
        x.save();
        x.globalAlpha = 0.45;
        x.drawImage(img, tx * CELL + CELL - size - 2, ty * CELL + CELL - size - 2, size, size);
        x.restore();
      }
    }
  }

  // hideouts — doors and vents set into the floor
  for (let ty = 0; ty < ROWS; ty += 1) {
    for (let tx = 0; tx < COLS; tx += 1) {
      const t = MAZE[ty][tx];
      if (t !== "D" && t !== "V") continue;
      const img = t === "D" ? sprites.door : sprites.vent;
      if (img?.complete && img.naturalWidth) {
        x.drawImage(img, tx * CELL + 2, ty * CELL + 2, CELL - 4, CELL - 4);
      } else {
        x.fillStyle = t === "D" ? "#2c1f10" : "#12262b";
        x.fillRect(tx * CELL + 3, ty * CELL + 3, CELL - 6, CELL - 6);
      }
    }
  }

  // sanctuary pad — the lit safehouse tile at the maze heart
  let sanctuary = null;
  for (let ty = 0; ty < ROWS; ty += 1) {
    for (let tx = 0; tx < COLS; tx += 1) {
      if (MAZE[ty][tx] !== "S") continue;
      sanctuary = { x: tx * CELL + CELL / 2, y: ty * CELL + CELL / 2 };
      const px = tx * CELL;
      const py = ty * CELL;
      x.fillStyle = "rgba(70, 224, 212, 0.14)";
      x.fillRect(px, py, CELL, CELL);
      x.strokeStyle = "rgba(70, 224, 212, 0.7)";
      x.lineWidth = 2;
      x.strokeRect(px + 3, py + 3, CELL - 6, CELL - 6);
      x.fillStyle = "rgba(70, 224, 212, 0.85)";
      x.font = `900 8px ${MONO}`;
      x.textAlign = "center";
      x.fillText("SAFE", px + CELL / 2, py + CELL / 2 + 3);
    }
  }

  // street lamps — deterministic intersections; these cut light holes at night
  const lamps = [];
  for (let ty = 1; ty < ROWS - 1; ty += 1) {
    for (let tx = 1; tx < COLS - 1; tx += 1) {
      if (MAZE[ty][tx] !== ".") continue;
      if (hash01(tx * 13 + 1, ty * 7 + 3) > 0.045) continue;
      const cx = tx * CELL + CELL / 2;
      const cy = ty * CELL + CELL / 2;
      lamps.push({ x: cx, y: cy });
      const img = sprites.lamp;
      if (img?.complete && img.naturalWidth) {
        x.drawImage(img, cx - 16, cy - 16, 32, 32);
      } else {
        x.fillStyle = "#3a3320";
        x.beginPath();
        x.arc(cx, cy, 5, 0, Math.PI * 2);
        x.fill();
      }
    }
  }
  // tonight's sky washes the whole maze — CRT green, dusk red, or plain ash
  if (skin?.wash) {
    x.fillStyle = skin.wash;
    x.fillRect(0, 0, W, H);
  }
  return { canvas: c, lamps, sanctuary };
}

/* ---------- helpers ---------- */
const blit = (ctx, img, cx, cy, size, { flip = false, alpha = 1 } = {}) => {
  if (!img?.complete || !img.naturalWidth) return false;
  const w = (size * img.naturalWidth) / img.naturalHeight;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.imageSmoothingEnabled = false;
  if (flip) {
    ctx.translate(cx, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(img, -w / 2, cy - size / 2, w, size);
  } else {
    ctx.drawImage(img, cx - w / 2, cy - size / 2, w, size);
  }
  ctx.restore();
  return true;
};

const runnerSprite = (sprites, r, frame) => {
  // DoloPals runners draw their key art (single frame, flipped when heading west)
  if (r.character && sprites.characters?.[r.character]) {
    return { img: sprites.characters[r.character], flip: r.dir === "w" };
  }
  const set = r.dir === "w" ? sprites.dolo.e : sprites.dolo[r.dir] || sprites.dolo.s;
  const idx = r.moving && set.length > 1 ? Math.floor(frame / 9) % set.length : 0;
  return { img: set[idx % set.length], flip: r.dir === "w" };
};

/* ---------- main render ---------- */
export function renderNight(g, ctx, { sprites, staticRef, darkRef }) {
  if (!staticRef.current) staticRef.current = buildStatic(sprites, g.skin);
  const skin = g.skin;
  const stat = staticRef.current;
  ctx.drawImage(stat.canvas, 0, 0);

  // side doors — sealed slabs until the last stretch, then lit ways out (left + right)
  for (const door of EXITS) {
    const ex = door.x * CELL;
    const ey = door.y * CELL;
    if (g.exitOpen) {
      const pulse = 0.6 + Math.sin(g.frame * 0.12) * 0.25;
      ctx.fillStyle = `rgba(70, 224, 212, ${pulse})`;
      ctx.fillRect(ex + 3, ey + 3, CELL - 6, CELL - 6);
      ctx.fillStyle = "#04110f";
      ctx.font = `800 9px ${MONO}`;
      ctx.textAlign = "center";
      ctx.fillText("OUT", ex + CELL / 2, ey + CELL / 2 + 3);
    } else {
      ctx.fillStyle = "#3a0d14";
      ctx.fillRect(ex + 2, ey + 2, CELL - 4, CELL - 4);
      ctx.strokeStyle = "rgba(255, 51, 85, 0.8)";
      ctx.lineWidth = 2;
      ctx.strokeRect(ex + 4, ey + 4, CELL - 8, CELL - 8);
      // hazard chevrons
      ctx.strokeStyle = "rgba(255, 176, 32, 0.65)";
      ctx.beginPath();
      for (let i = 0; i < 3; i += 1) {
        ctx.moveTo(ex + 8 + i * 9, ey + CELL - 10);
        ctx.lineTo(ex + 14 + i * 9, ey + 10);
      }
      ctx.stroke();
    }
  }

  // sanctuary pulse — the safehouse breathes
  if (stat.sanctuary) {
    const pulse = 15 + Math.sin(g.frame * 0.08) * 4;
    ctx.save();
    ctx.strokeStyle = "rgba(70, 224, 212, 0.55)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(stat.sanctuary.x, stat.sanctuary.y, pulse, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  // traps — armed ones blink hot, arming ones are dim
  for (const t of g.traps) {
    const armed = t.armed <= 0;
    const blink = Math.floor(g.frame / 12) % 2 === 0;
    ctx.save();
    ctx.globalAlpha = armed ? 1 : 0.45;
    ctx.strokeStyle = armed && blink ? "#ff3355" : "#8a4b3c";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(t.x, t.y, 11, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    for (let i = 0; i < 6; i += 1) {
      const a = (i / 6) * Math.PI * 2 + g.frame * 0.02;
      ctx.moveTo(t.x + Math.cos(a) * 5, t.y + Math.sin(a) * 5);
      ctx.lineTo(t.x + Math.cos(a) * 11, t.y + Math.sin(a) * 11);
    }
    ctx.stroke();
    ctx.restore();
  }

  // pickups — bobbing glints in the dark
  for (const p of g.pickups) {
    const bob = Math.sin((g.frame + p.x) * 0.08) * 2.5;
    let img = null;
    if (p.kind === "can") img = sprites.supply[(p.tx + p.ty) % sprites.supply.length];
    else if (p.kind === "medkit") img = sprites.medkit;
    else if (p.kind === "lore") img = sprites.lore[(p.tx + p.ty) % sprites.lore.length];
    else if (p.kind === "crate") img = sprites.crate;
    else if (p.kind === "power") img = sprites.powerIcons[p.power];
    const size = p.kind === "power" ? 30 : 26;
    if (!blit(ctx, img, p.x, p.y + bob, size)) {
      ctx.fillStyle = p.kind === "power" ? "#46e0d4" : p.kind === "medkit" ? "#ff5a6e" : p.kind === "lore" ? "#ffd76a" : "#9db4c0";
      ctx.beginPath();
      ctx.arc(p.x, p.y + bob, p.kind === "power" ? 8 : 6, 0, Math.PI * 2);
      ctx.fill();
    }
    if (p.kind === "power") {
      ctx.save();
      ctx.globalAlpha = 0.5 + Math.sin(g.frame * 0.1) * 0.3;
      ctx.strokeStyle = "#46e0d4";
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(p.x, p.y + bob, 15, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }
  }

  // boombox lure — throbbing speaker
  if (g.lure) {
    const beat = 1 + Math.sin(g.frame * 0.35) * 0.25;
    ctx.save();
    ctx.translate(g.lure.x, g.lure.y);
    ctx.scale(beat, beat);
    ctx.fillStyle = "#ffb020";
    ctx.fillRect(-9, -7, 18, 14);
    ctx.fillStyle = "#1a1a1a";
    ctx.beginPath();
    ctx.arc(-4, 0, 3.4, 0, Math.PI * 2);
    ctx.arc(4, 0, 3.4, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
    ctx.save();
    ctx.globalAlpha = 0.35;
    ctx.strokeStyle = "#ffb020";
    ctx.beginPath();
    ctx.arc(g.lure.x, g.lure.y, 16 + (g.frame % 20), 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  // runners — dolo frames, color ring + name tag per runner
  for (const r of g.runners) {
    if (r.escaped) continue;
    const down = r.down > 0;
    const blinkOff = r.invuln > 0 && Math.floor(g.frame / 6) % 2 === 0;
    if (blinkOff) continue;
    // team-color ring
    ctx.save();
    ctx.globalAlpha = r.human ? 0.95 : 0.6;
    ctx.strokeStyle = r.color;
    ctx.lineWidth = r.human ? 2.5 : 1.5;
    ctx.beginPath();
    ctx.arc(r.x, r.y + 14, 17, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
    const { img, flip } = runnerSprite(sprites, r, g.frame);
    const size = r.fx.adrenaline > 0 ? 52 : 46;
    const alpha = r.fx.smoke > 0 ? 0.35 : down ? 0.55 : 1;
    if (!blit(ctx, img, r.x, r.y, size, { flip, alpha })) {
      ctx.fillStyle = r.color;
      ctx.globalAlpha = alpha;
      ctx.beginPath();
      ctx.arc(r.x, r.y, 15, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
    }
    // sword slash — the red blade: a glowing crimson crescent sweeping the facing arc
    if (r.slashT > 0) {
      const ang = { e: 0, s: Math.PI / 2, w: Math.PI, n: -Math.PI / 2 }[r.dir] ?? Math.PI / 2;
      const p = 1 - r.slashT / SLASH_FRAMES;
      ctx.save();
      ctx.translate(r.x, r.y);
      ctx.rotate(ang);
      ctx.lineCap = "round";
      ctx.shadowColor = "rgba(255, 0, 51, 0.95)";
      ctx.shadowBlur = 18;
      ctx.strokeStyle = `rgba(255, 26, 51, ${0.95 - p * 0.85})`;
      ctx.lineWidth = 7;
      ctx.beginPath();
      ctx.arc(0, 0, 24 + p * 26, -0.95, 0.95);
      ctx.stroke();
      ctx.strokeStyle = `rgba(255, 190, 200, ${0.9 - p * 0.9})`;
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.arc(0, 0, 17 + p * 31, -0.75, 0.75);
      ctx.stroke();
      ctx.restore();
    }
    if (down) {
      ctx.fillStyle = "#ff3355";
      ctx.font = `800 10px ${MONO}`;
      ctx.textAlign = "center";
      ctx.fillText("DOWN", r.x, r.y - 28);
    } else {
      ctx.fillStyle = r.human ? "#ffffff" : r.color;
      ctx.font = `800 9px ${MONO}`;
      ctx.textAlign = "center";
      ctx.fillText(r.human ? "YOU" : r.name, r.x, r.y - 26);
    }
    if (r.armor) {
      ctx.strokeStyle = "rgba(157, 180, 192, 0.9)";
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(r.x, r.y, 21, 0, Math.PI * 2);
      ctx.stroke();
    }
    if (r.item) {
      const icon = sprites.powerIcons[r.item];
      if (icon?.complete && icon.naturalWidth) ctx.drawImage(icon, r.x + 13, r.y - 32, 16, 16);
    }
  }

  // zombies — shambling horde
  for (const z of g.zombies) {
    if (z.respawnIn > 0 || z.x < 0) continue;
    const img = sprites.zombies[z.kind % sprites.zombies.length];
    const sway = Math.sin((g.frame + z.x) * 0.06) * 2;
    if (!blit(ctx, img, z.x + sway, z.y, 42)) {
      ctx.fillStyle = "#5f7a3a";
      ctx.beginPath();
      ctx.arc(z.x, z.y, 14, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  // mutated dogs — low slinking bodies, red wind-up on charge
  for (const d of g.dogs) {
    const img = sprites.dogs[d.kind % sprites.dogs.length];
    if (d.state === "charge" && Math.floor(g.frame / 4) % 2 === 0) {
      ctx.save();
      ctx.globalAlpha = 0.4;
      ctx.fillStyle = "#ff3355";
      ctx.beginPath();
      ctx.arc(d.x, d.y, 22, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
    if (!blit(ctx, img, d.x, d.y, 40, { flip: (d.state === "charge" ? d.chargeDir : d.dir) < 0, alpha: d.stun > 0 ? 0.5 : 1 })) {
      ctx.fillStyle = "#8a5a2b";
      ctx.beginPath();
      ctx.ellipse(d.x, d.y, 16, 10, 0, 0, Math.PI * 2);
      ctx.fill();
    }
    if (d.stun > 0) {
      ctx.fillStyle = "#ffd76a";
      ctx.font = `800 9px ${MONO}`;
      ctx.textAlign = "center";
      ctx.fillText("STUNNED", d.x, d.y - 22);
    }
  }

  /* ---------- darkness mask ---------- */
  if (!darkRef.current) {
    darkRef.current = document.createElement("canvas");
    darkRef.current.width = W;
    darkRef.current.height = H;
  }
  const dark = darkRef.current;
  const dctx = dark.getContext("2d");
  dctx.globalCompositeOperation = "source-over";
  dctx.clearRect(0, 0, W, H);
  // dawn lifts the dark over the final stretch
  const lift = g.framesLeft < 12 * 60 ? (1 - g.framesLeft / (12 * 60)) * 0.35 : 0;
  dctx.fillStyle = `rgba(${skin?.dark || "3, 4, 12"}, ${0.84 - lift})`;
  dctx.fillRect(0, 0, W, H);
  dctx.globalCompositeOperation = "destination-out";
  const hole = (hx, hy, r, core = 0.55) => {
    const grad = dctx.createRadialGradient(hx, hy, r * core * 0.4, hx, hy, r);
    grad.addColorStop(0, "rgba(0,0,0,1)");
    grad.addColorStop(1, "rgba(0,0,0,0)");
    dctx.fillStyle = grad;
    dctx.beginPath();
    dctx.arc(hx, hy, r, 0, Math.PI * 2);
    dctx.fill();
  };
  // lamp light with a mains flicker
  for (const l of stat.lamps) hole(l.x, l.y, 98 + Math.sin((g.frame + l.x) * 0.11) * 7);
  // the sanctuary burns brightest — a safe pool of light the horde can't enter
  if (stat.sanctuary) hole(stat.sanctuary.x, stat.sanctuary.y, 105);
  // runners carry a dull glow; flare turns night to day; smoke shrinks you to nothing
  for (const r of g.runners) {
    if (r.escaped || r.down > 0) continue;
    const rad = r.fx.flare > 0 ? 230 : r.fx.smoke > 0 ? 55 : 122;
    hole(r.x, r.y, rad);
  }
  for (const p of g.pickups) if (p.kind === "power") hole(p.x, p.y, 34, 0.4);
  if (g.lure) hole(g.lure.x, g.lure.y, 95);
  if (g.exitOpen) for (const door of EXITS) hole(door.x * CELL + CELL / 2, door.y * CELL + CELL / 2, 140);
  ctx.drawImage(dark, 0, 0);

  // eyes in the dark — the horde is never fully gone
  ctx.save();
  for (const z of g.zombies) {
    if (z.respawnIn > 0 || z.x < 0) continue;
    ctx.fillStyle = "rgba(255, 60, 60, 0.55)";
    ctx.fillRect(z.x - 5, z.y - 4, 3, 2);
    ctx.fillRect(z.x + 2, z.y - 4, 3, 2);
  }
  for (const d of g.dogs) {
    ctx.fillStyle = "rgba(255, 176, 32, 0.6)";
    ctx.fillRect(d.x - 5, d.y - 5, 3, 2);
    ctx.fillRect(d.x + 2, d.y - 5, 3, 2);
  }
  ctx.restore();

  // tonight's sky tints everything the light touches — before the HUD so it stays crisp
  if (skin?.wash) {
    ctx.save();
    ctx.fillStyle = skin.wash;
    ctx.fillRect(0, 0, W, H);
    ctx.restore();
  }

  /* ---------- HUD ---------- */
  ctx.save();
  ctx.fillStyle = "rgba(2, 2, 6, 0.72)";
  ctx.fillRect(0, 0, W, 30);
  ctx.strokeStyle = "rgba(255, 51, 85, 0.35)";
  ctx.beginPath();
  ctx.moveTo(0, 30.5);
  ctx.lineTo(W, 30.5);
  ctx.stroke();

  // night clock
  const pct = 1 - g.framesLeft / NIGHT_FRAMES;
  ctx.fillStyle = "#8c7b75";
  ctx.font = `800 9px ${MONO}`;
  ctx.textAlign = "left";
  ctx.fillText("NIGHT", 8, 19);
  ctx.fillStyle = "rgba(255,255,255,0.12)";
  ctx.fillRect(46, 10, 70, 10);
  const dawnColor = pct > 0.8 ? "#ff9a3c" : "#5a7bff";
  ctx.fillStyle = dawnColor;
  ctx.fillRect(46, 10, 70 * pct, 10);
  ctx.fillStyle = "#e8ded8";
  ctx.fillText(pct > 0.92 ? "DAWN" : `${Math.ceil(g.framesLeft / 60)}S`, 122, 19);

  // runner chips — compact: color tick, name, supplies
  let cx = 158;
  for (const r of g.runners) {
    ctx.fillStyle = r.escaped ? "#46e0d4" : r.down > 0 ? "#ff3355" : r.color;
    ctx.fillRect(cx, 11, 7, 7);
    ctx.fillStyle = r.human ? "#ffffff" : "#b9aaa3";
    ctx.font = `800 9px ${MONO}`;
    ctx.textAlign = "left";
    const label = `${r.human ? "YOU" : r.name} ${r.supplies}${r.escaped ? " OUT" : r.down > 0 ? " X" : ""}`;
    ctx.fillText(label, cx + 10, 18);
    cx += 10 + ctx.measureText(label).width + 12;
  }

  // P1 inventory — slash pip, item, trap charges
  const me = g.runners.find((r) => r.human);
  if (me) {
    ctx.textAlign = "right";
    ctx.font = `800 9px ${MONO}`;
    ctx.fillStyle = me.trapCharges > 0 ? "#ffb020" : "#8c7b75";
    ctx.fillText(`[T]×${me.trapCharges}`, W - 8, 19);
    const trapsW = ctx.measureText(`[T]×${me.trapCharges}`).width;
    ctx.fillStyle = me.item ? "#46e0d4" : "#8c7b75";
    ctx.fillText(me.item ? `[E] ${POWER_ITEMS[me.item].name}` : "NO ITEM", W - 14 - trapsW, 19);
    const itemW = ctx.measureText(me.item ? `[E] ${POWER_ITEMS[me.item].name}` : "NO ITEM").width;
    // slash readiness — a small red crossed-blade tick that dims while cooling down
    const sx = W - 26 - trapsW - itemW;
    ctx.strokeStyle = me.slashCd <= 0 ? "#ff1a33" : "rgba(255,26,51,0.25)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(sx - 5, 16);
    ctx.lineTo(sx + 3, 8);
    ctx.moveTo(sx - 5, 8);
    ctx.lineTo(sx - 1, 12);
    ctx.stroke();
  }
  ctx.restore();

  // banner — big transmission line under the HUD
  if (g.bannerTimer > 0 && g.banner) {
    ctx.save();
    ctx.globalAlpha = Math.min(1, g.bannerTimer / 30);
    ctx.textAlign = "center";
    ctx.font = `900 13px ${MONO}`;
    let tw = ctx.measureText(g.banner).width;
    if (tw > W - 60) {
      ctx.font = `900 10px ${MONO}`;
      tw = ctx.measureText(g.banner).width;
    }
    ctx.fillStyle = "rgba(2,2,6,0.65)";
    ctx.fillRect(W / 2 - tw / 2 - 16, 40, tw + 32, 26);
    ctx.fillStyle = skin?.accent || "#ff3355";
    ctx.fillText(g.banner, W / 2, 58);
    ctx.restore();
  }

  // final ten seconds — red dawn pulse at the edges
  if (g.framesLeft < 600 && !g.over) {
    ctx.save();
    ctx.globalAlpha = (1 - g.framesLeft / 600) * (0.12 + Math.sin(g.frame * 0.08) * 0.05);
    ctx.strokeStyle = "#ff3355";
    ctx.lineWidth = 18;
    ctx.strokeRect(0, 0, W, H);
    ctx.restore();
  }

  if (g.over) {
    ctx.save();
    ctx.fillStyle = "rgba(2,2,6,0.45)";
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#ff9a3c";
    ctx.font = `900 34px ${HEADING}`;
    ctx.textAlign = "center";
    ctx.fillText("DAWN BREAKS", W / 2, H / 2 - 10);
    ctx.restore();
  }
}
